import React from 'react';
import {
  useCreateBlockNote,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  FormattingToolbarController,
  FormattingToolbar,
  FilePanelController,
  BasicTextStyleButton,
  ColorStyleButton,
  NestBlockButton,
  UnnestBlockButton,
  useBlockNoteEditor,
  useEditorState,
  useComponentsContext,
  useDictionary,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/ariakit';
import { filterSuggestionItems } from '@blocknote/core';
import { useTheme } from 'next-themes';
import { useAIHighlightPreference } from '@/hooks/useAIHighlightPreference';
import { RiArrowDownSLine, RiH1, RiH2, RiH3, RiLink, RiListCheck3, RiListOrdered, RiListUnordered, RiPlayList2Fill, RiQuoteText, RiText } from 'react-icons/ri';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { attachEmbedUpgrader, resolveEmbedUrl } from './mediaEmbedUpgrader';





declare global {
  interface Window {
    __mindnoteLatestBlockNoteMarkdown?: string;
    getBlockNoteMarkdown?: () => Promise<string>;
    insertAtBlockNoteCaret?: (rawMd: string) => Promise<boolean>;
    replaceBlockNoteMarkdown?: (markdown: string) => Promise<boolean>;
    appendToBlockNoteEnd?: (rawMd: string) => Promise<string | false>;
  }
}

const AI_START_MARKER = '【✨AI▸ ';
const AI_END_MARKER = ' ◂AI✨】';
const TOGGLE_MARKER = '\u2063MNTGL\u2063';
const IMAGE_SIZE_MARKER_RE = /(?:#|&)mnw=(\d+)(?:&mnh=(\d+))?$/;
type MediaKind = 'image' | 'video' | 'audio';

// Markdown has no native toggle list. To preserve toggleListItem blocks
// across save/reload, we temporarily rewrite them as bulletListItem with an
// invisible marker prepended to the content. On parse we reverse it.
const markTogglesForSerialize = (blocks: any[]): any[] =>
  blocks.map((b) => {
    const nb: any = { ...b };
    if (Array.isArray(b?.children) && b.children.length) {
      nb.children = markTogglesForSerialize(b.children);
    }
    if (b?.type === 'toggleListItem') {
      nb.type = 'bulletListItem';
      const content = Array.isArray(b.content) ? b.content : [];
      nb.content = [{ type: 'text', text: TOGGLE_MARKER, styles: {} }, ...content];
    }
    return nb;
  });

const unmarkTogglesAfterParse = (blocks: any[]) => {
  for (const b of blocks) {
    if (b?.children?.length) unmarkTogglesAfterParse(b.children);
    if (b?.type === 'bulletListItem' && Array.isArray(b.content)) {
      const first = b.content[0];
      if (first?.type === 'text' && typeof first.text === 'string' && first.text.startsWith(TOGGLE_MARKER)) {
        b.type = 'toggleListItem';
        first.text = first.text.slice(TOGGLE_MARKER.length);
        if (first.text === '') b.content.shift();
      }
    }
  }
};

const stripImageSizeMarker = (url: string) => url.replace(IMAGE_SIZE_MARKER_RE, '');
const appendImageSizeMarker = (url: string, width: number) => {
  const cleanUrl = stripImageSizeMarker(url);
  return `${cleanUrl}${cleanUrl.includes('#') ? '&' : '#'}mnw=${Math.round(width)}`;
};

const getDirectMediaKind = (url: string): Exclude<MediaKind, 'image'> | null => {
  const cleanUrl = stripImageSizeMarker(url).trim();
  if (/^data:video\//i.test(cleanUrl) || /^blob:/i.test(cleanUrl) && /video/i.test(cleanUrl)) return 'video';
  if (/^data:audio\//i.test(cleanUrl) || /^blob:/i.test(cleanUrl) && /audio/i.test(cleanUrl)) return 'audio';
  const path = cleanUrl.split(/[?#]/)[0].toLowerCase();
  if (/\.(mp4|m4v|mov|webm|ogv|m3u8)$/i.test(path)) return 'video';
  if (/\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|weba)$/i.test(path)) return 'audio';
  // Known embed-providers (YouTube, Vimeo, SoundCloud, Spotify, etc.) are
  // promoted to video/audio blocks so the embed upgrader can render them
  // inline as iframes instead of leaving them as plain clickable links.
  try {
    const embed = resolveEmbedUrl(cleanUrl);
    if (embed) return embed.kind;
  } catch { /* ignore — not in a browser context */ }
  return null;
};

const escapeAttribute = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const mediaTagFromUrl = (url: string, name = '') => {
  const kind = getDirectMediaKind(url);
  if (!kind) return null;
  const safeUrl = escapeAttribute(url);
  const safeName = name ? ` data-name="${escapeAttribute(name)}"` : '';
  return kind === 'video'
    ? `<video src="${safeUrl}"${safeName} controls></video>`
    : `<audio src="${safeUrl}" controls></audio>`;
};

const expandDirectMediaLinksToBlocks = (markdown: string) => {
  const imageExpanded = markdown.replace(
    /!\[([^\]]*)\]\(([^)\s]+)(\s+(?:"[^"]*"|'[^']*'))?\)/g,
    (match, alt, url) => mediaTagFromUrl(url, alt) ?? match,
  );

  return imageExpanded.replace(
    /(^|[^!])\[([^\]]*)\]\(([^)\s]+)(\s+(?:"[^"]*"|'[^']*'))?\)/g,
    (match, prefix, label, url) => {
      const mediaTag = mediaTagFromUrl(url, label);
      return mediaTag ? `${prefix}${mediaTag}` : match;
    },
  );
};

const collectResizableMedia = (blocks: any[], media: Array<{ type: 'image' | 'video'; url: string; width?: number }>) => {
  for (const block of blocks) {
    const width = Number(block?.props?.previewWidth);
    if ((block?.type === 'image' || block?.type === 'video') && block?.props?.url) {
      media.push({
        type: block.type,
        url: stripImageSizeMarker(block.props.url),
        width: Number.isFinite(width) && width > 0 ? Math.round(width) : undefined,
      });
    }
    if (block?.children?.length) collectResizableMedia(block.children, media);
  }
};

const persistMediaSizesInMarkdown = (markdown: string, blocks: any[]) => {
  const media: Array<{ type: 'image' | 'video'; url: string; width?: number }> = [];
  collectResizableMedia(blocks, media);
  if (media.length === 0) return markdown;

  let index = 0;
  return markdown.replace(
    /!\[([^\]]*)\]\(([^)\s]+)(\s+(?:"[^"]*"|'[^']*'))?\)|<(img|video)\b([^>]*?\s+src="([^"]+)"[^>]*)>/gi,
    (match, alt, mdUrl, title = '', tagName, attrs, htmlUrl) => {
      const item = media[index++];
      if (!item) return match;
      const url = mdUrl || htmlUrl || item.url;
      const sizedUrl = item.width ? appendImageSizeMarker(url, item.width) : stripImageSizeMarker(url);
      if (mdUrl) {
        if (item.type === 'video') return mediaTagFromUrl(sizedUrl, alt) ?? `<video src="${escapeAttribute(sizedUrl)}" controls></video>`;
        return `![${alt}](${sizedUrl}${title})`;
      }
      return `<${tagName}${attrs.replace(/\ssrc="[^"]+"/i, ` src="${sizedUrl}"`)}>`;
    }
  );
};

interface BlockNoteEditorProps {
  content: string;
  onContentChange?: (markdown: string) => void;
  externalRevision?: number;
  backgroundColor: string;
  editable?: boolean;
  onElementRef?: (el: HTMLDivElement | null) => void;
  onTextSelection?: () => void;
}

type IOSBlockMenuState = {
  open: boolean;
  activeItemId: string;
  selectedBlockIds: string[];
};

type IOSLinkDialogState = {
  open: boolean;
  from: number;
  text: string;
  url: string;
};

type IOSBlockItem = {
  id: string;
  label: string;
  type: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  props?: Record<string, string | number | boolean>;
};

const getIOSBlockItems = (editor: any): IOSBlockItem[] => [
  { id: 'paragraph', label: editor.dictionary.slash_menu.paragraph.title, type: 'paragraph', icon: RiText },
  { id: 'heading-1', label: editor.dictionary.slash_menu.heading.title, type: 'heading', icon: RiH1, props: { level: 1, isToggleable: false } },
  { id: 'heading-2', label: editor.dictionary.slash_menu.heading_2.title, type: 'heading', icon: RiH2, props: { level: 2, isToggleable: false } },
  { id: 'heading-3', label: editor.dictionary.slash_menu.heading_3.title, type: 'heading', icon: RiH3, props: { level: 3, isToggleable: false } },
  { id: 'quote', label: editor.dictionary.slash_menu.quote.title, type: 'quote', icon: RiQuoteText },
  { id: 'toggle-list', label: editor.dictionary.slash_menu.toggle_list.title, type: 'toggleListItem', icon: RiPlayList2Fill },
  { id: 'bullet-list', label: editor.dictionary.slash_menu.bullet_list.title, type: 'bulletListItem', icon: RiListUnordered },
  { id: 'numbered-list', label: editor.dictionary.slash_menu.numbered_list.title, type: 'numberedListItem', icon: RiListOrdered },
  { id: 'check-list', label: editor.dictionary.slash_menu.check_list.title, type: 'checkListItem', icon: RiListCheck3 },
];

const matchesIOSBlockItem = (block: any, item: IOSBlockItem) =>
  block?.type === item.type && Object.entries(item.props ?? {}).every(([key, value]) => block?.props?.[key] === value);

const normalizeLinkUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(trimmed) || /^(mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const IOSFormattingToolbar = ({
  onOpenBlockMenu,
  onOpenLinkDialog,
}: {
  onOpenBlockMenu: (payload: { activeItemId: string; selectedBlockIds: string[] }) => void;
  onOpenLinkDialog: (payload: { from: number; text: string; url: string }) => void;
}) => {
  const editor = useBlockNoteEditor<any, any, any>();
  const toolbarState = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor.isEditable) return undefined;
      const selectedBlocks = editor.getSelection()?.blocks || [editor.getTextCursorPosition().block];
      if (!selectedBlocks.find((block: any) => block.content !== undefined)) return undefined;

      return {
        firstBlock: selectedBlocks[0],
        selectedBlockIds: selectedBlocks.map((block: any) => block.id),
        selectedText: editor.getSelectedText() ?? '',
        selectedLinkUrl: editor.getSelectedLinkUrl() ?? '',
        from: editor.prosemirrorState.selection.from,
      };
    },
  });

  if (!toolbarState) return null;

  const blockItems = getIOSBlockItems(editor);
  const currentBlockItem = blockItems.find((item) => matchesIOSBlockItem(toolbarState.firstBlock, item)) ?? blockItems[0];
  const CurrentIcon = currentBlockItem.icon;
  const preventToolbarBlur = (event: React.MouseEvent | React.PointerEvent) => event.preventDefault();

  return (
    <FormattingToolbar>
      <button
        type="button"
        className="bn-ak-button bn-ak-secondary"
        onMouseDown={preventToolbarBlur}
        onPointerDown={preventToolbarBlur}
        onClick={() => {
          if (toolbarState.selectedText.length > 0 && toolbarState.selectedBlockIds.length > 0) {
            try {
              editor.focus();
              editor.setSelection(
                toolbarState.selectedBlockIds[0],
                toolbarState.selectedBlockIds[toolbarState.selectedBlockIds.length - 1],
              );
            } catch {
              // Keep the menu usable even if selection normalization fails.
            }
          }

          onOpenBlockMenu({ activeItemId: currentBlockItem.id, selectedBlockIds: toolbarState.selectedBlockIds });
        }}
      >
        <CurrentIcon size={16} />
        <span>{currentBlockItem.label}</span>
        <RiArrowDownSLine size={16} />
      </button>
      <BasicTextStyleButton basicTextStyle="bold" />
      <BasicTextStyleButton basicTextStyle="italic" />
      <BasicTextStyleButton basicTextStyle="underline" />
      <BasicTextStyleButton basicTextStyle="strike" />
      <BasicTextStyleButton basicTextStyle="code" />
      <ColorStyleButton />
      <NestBlockButton />
      <UnnestBlockButton />
      <button
        type="button"
        className="bn-ak-button bn-ak-secondary"
        onMouseDown={preventToolbarBlur}
        onPointerDown={preventToolbarBlur}
        onClick={() => onOpenLinkDialog({ from: toolbarState.from, text: toolbarState.selectedText, url: toolbarState.selectedLinkUrl })}
        aria-label="Link"
      >
        <RiLink size={16} />
      </button>
    </FormattingToolbar>
  );
};

const SingleEmbedFilePanel = ({ blockId }: { blockId: string }) => {
  const editor = useBlockNoteEditor<any, any, any>();
  const components = useComponentsContext();
  const dictionary = useDictionary();
  const [url, setUrl] = React.useState('');
  const block = editor.getBlock(blockId) as any;
  if (!components || !block) return null;
  const tabName = dictionary.file_panel.embed.title;

  const submit = () => {
    const normalizedUrl = normalizeLinkUrl(url);
    if (!normalizedUrl) return;
    editor.updateBlock(blockId, { props: { name: normalizedUrl, url: normalizedUrl } });
  };

  const panel = (
    <components.FilePanel.TabPanel className="bn-tab-panel">
      <components.FilePanel.TextInput
        className="bn-text-input"
        placeholder={dictionary.file_panel.embed.url_placeholder}
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            submit();
          }
        }}
      />
      <components.FilePanel.Button className="bn-button mn-single-embed-submit" onClick={submit}>
        {dictionary.file_panel.embed.embed_button[block.type] || dictionary.file_panel.embed.embed_button.file}
      </components.FilePanel.Button>
    </components.FilePanel.TabPanel>
  );

  return (
    <components.FilePanel.Root
      className="bn-panel"
      defaultOpenTab={tabName}
      openTab={tabName}
      setOpenTab={() => {}}
      loading={false}
      tabs={[{ name: tabName, tabPanel: panel }]}
    />
  );
};


const BlockNoteEditorWrapper: React.FC<BlockNoteEditorProps> = ({
  content,
  onContentChange,
  externalRevision = 0,
  backgroundColor,
  editable = true,
  onElementRef,
  onTextSelection,
}) => {
  const { theme } = useTheme();
  const [showAIHighlights] = useAIHighlightPreference();
  const editor = useCreateBlockNote();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const lastSelectionRangeRef = React.useRef<Range | null>(null);
  const hydratedRef = React.useRef(false);
  const lastEmittedRef = React.useRef<string>('');
  const hydratingRef = React.useRef(false);
  const lastExternalRevisionRef = React.useRef(externalRevision);
  const lastShowAIHighlightsRef = React.useRef(showAIHighlights);
  const changeSequenceRef = React.useRef(0);
  const lastCursorBlockIdRef = React.useRef<string | null>(null);
  const [isIOS, setIsIOS] = React.useState(false);
  const [iosBlockMenuState, setIosBlockMenuState] = React.useState<IOSBlockMenuState>({
    open: false,
    activeItemId: 'paragraph',
    selectedBlockIds: [],
  });
  const [iosLinkDialogState, setIosLinkDialogState] = React.useState<IOSLinkDialogState>({
    open: false,
    from: 0,
    text: '',
    url: '',
  });

  React.useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    setIsIOS(/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  }, []);


  React.useEffect(() => {
    onElementRef?.(containerRef.current);
    return () => onElementRef?.(null);
  }, [onElementRef]);

  // Replace BlockNote's <video>/<audio> with iframe embeds for known providers
  // (YouTube, Vimeo, SoundCloud, Spotify, ...). Direct media files keep their
  // native HTML5 player. Runs in both editable and read-only (shared) modes.
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    return attachEmbedUpgrader(root);
  }, [content, externalRevision]);

  React.useEffect(() => {
    return editor.onSelectionChange(() => {
      try {
        const cursor = editor.getTextCursorPosition();
        if (cursor?.block?.id) lastCursorBlockIdRef.current = cursor.block.id;
      } catch {
        // Selection can be temporarily unavailable while focus is outside BlockNote.
      }
    });
  }, [editor]);

  // Track text selection inside the BlockNote container so the toolbar
  // (comment button, search-and-replace, etc.) can read the selected text.
  React.useEffect(() => {
    const handleSelectionChange = () => {
      const div = containerRef.current;
      if (!div) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!div.contains(range.commonAncestorContainer)) return;

      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(div);
      preCaretRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preCaretRange.toString().length;
      const selectedText = range.toString();
      const endOffset = startOffset + selectedText.length;

      // Always track caret position (used for AI insertions).
      (div as any).caretOffset = startOffset;
      (div as any).value = div.textContent || '';

      // Only overwrite the saved selection when the user has actually
      // selected something. Otherwise a toolbar mousedown (which collapses
      // the selection) would erase the last real selection before the
      // comment / link button can read it.
      if (selectedText.length > 0) {
        lastSelectionRangeRef.current = range.cloneRange();
        (div as any).selectionStart = startOffset;
        (div as any).selectionEnd = endOffset;
        (div as any).selectedText = selectedText;
        onTextSelection?.();
      } else if (sel.isCollapsed && div.contains(sel.anchorNode)) {
        // User placed the caret somewhere inside the editor without
        // selecting anything. Clear any previously saved selection so
        // downstream consumers (AI panel "Selected text will be modified",
        // etc.) don't keep targeting a stale range.
        (div as any).selectionStart = startOffset;
        (div as any).selectionEnd = startOffset;
        (div as any).selectedText = '';
        onTextSelection?.();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [onTextSelection]);

  // Walk blocks recursively and restore image previewWidth from URL hash
  // (e.g. https://.../img.png#mnw=520) saved by the serializer below. This
  // lets resized images keep their size across save/reload.
  const restoreImageSizes = React.useCallback((blocks: any[]) => {
    for (const b of blocks) {
      if ((b?.type === 'image' || b?.type === 'video') && b?.props?.url) {
        const m = IMAGE_SIZE_MARKER_RE.exec(b.props.url);
        if (m) {
          b.props.previewWidth = parseInt(m[1], 10);
          b.props.url = stripImageSizeMarker(b.props.url);
        }
      }
      if (b?.children?.length) restoreImageSizes(b.children);
    }
  }, []);

  const serializeCurrentDocument = React.useCallback(async () => {
    const docForMd = markTogglesForSerialize(editor.document as any);
    let md = await editor.blocksToMarkdownLossy(docForMd as any);
    if (showAIHighlights) {
      md = md
        .split(AI_START_MARKER).join('<ai-generated>')
        .split(AI_END_MARKER).join('</ai-generated>');
    }
    md = md.replace(/<img\b([^>]*?)\s+src="([^"]+)"([^>]*?)\s+width="(\d+)"([^>]*)>/gi, (_match, before, url, middle, width, after) => {
      const alt = /\salt="([^"]*)"/i.exec(`${before} ${middle} ${after}`)?.[1] ?? '';
      return `![${alt}](${appendImageSizeMarker(url, parseInt(width, 10))})`;
    });
    return persistMediaSizesInMarkdown(md, editor.document);
  }, [editor, showAIHighlights]);

  const publishMarkdown = React.useCallback((md: string) => {
    lastEmittedRef.current = md;
    window.__mindnoteLatestBlockNoteMarkdown = md;
    if (containerRef.current) (containerRef.current as any).value = md;
    onContentChange?.(md);
  }, [onContentChange]);

  // Expose global helpers for tools that live outside the BlockNote tree.
  React.useEffect(() => {
    window.getBlockNoteMarkdown = async () => serializeCurrentDocument();

    const prepareAiMarkdownForEditor = (raw: string) => showAIHighlights
      ? raw
          .replace(/<ai-generated>/g, AI_START_MARKER)
          .replace(/<\/ai-generated>/g, AI_END_MARKER)
      : raw.replace(/<\/?ai-generated>/g, '');

    window.replaceBlockNoteMarkdown = async (markdown: string): Promise<boolean> => {
      try {
        const raw = markdown ?? '';
        const cleaned = expandDirectMediaLinksToBlocks(prepareAiMarkdownForEditor(raw));
        const blocks = cleaned.trim().length > 0
          ? await editor.tryParseMarkdownToBlocks(cleaned)
          : [{ type: 'paragraph' } as any];
        if (!blocks || blocks.length === 0) return false;

        restoreImageSizes(blocks);
        unmarkTogglesAfterParse(blocks);
        changeSequenceRef.current += 1;
        hydratingRef.current = true;
        try {
          editor.replaceBlocks(editor.document, blocks);
        } finally {
          window.setTimeout(() => {
            hydratingRef.current = false;
          }, 0);
        }
        publishMarkdown(raw);
        hydratedRef.current = true;
        lastExternalRevisionRef.current = externalRevision;
        return true;
      } catch (e) {
        console.warn('replaceBlockNoteMarkdown failed', e);
        return false;
      }
    };

    window.insertAtBlockNoteCaret = async (rawMd: string): Promise<boolean> => {
      try {
        if (!rawMd) return false;
        const prepared = expandDirectMediaLinksToBlocks(prepareAiMarkdownForEditor(rawMd));
        const blocks = await editor.tryParseMarkdownToBlocks(prepared);
        if (!blocks || blocks.length === 0) return false;
        const doc = editor.document;
        let referenceBlock: (typeof doc)[number] | null = null;
        try {
          const cursor = editor.getTextCursorPosition();
          referenceBlock = cursor?.block ?? null;
          if (referenceBlock?.id) lastCursorBlockIdRef.current = referenceBlock.id;
        } catch {
          referenceBlock = null;
        }
        if (!referenceBlock && lastCursorBlockIdRef.current) {
          referenceBlock = doc.find((block) => block?.id === lastCursorBlockIdRef.current) ?? null;
        }
        if (!referenceBlock) {
          referenceBlock = doc[doc.length - 1];
        }
        if (!referenceBlock) return false;

        changeSequenceRef.current += 1;
        hydratingRef.current = true;
        try {
          try {
            editor.focus();
            editor.setTextCursorPosition(referenceBlock.id, 'end');
          } catch { /* keep inserting even if focus restore fails */ }

          const isEmptyParagraph =
            referenceBlock?.type === 'paragraph' &&
            (!Array.isArray(referenceBlock.content) || referenceBlock.content.length === 0);
          if (isEmptyParagraph && doc.length === 1) {
            editor.replaceBlocks([referenceBlock.id], blocks);
          } else {
            editor.insertBlocks(blocks, referenceBlock.id, 'after');
          }
        } finally {
          window.setTimeout(() => {
            hydratingRef.current = false;
          }, 0);
        }

        // Explicitly serialize + publish so the parent state always picks up
        // the inserted content, even if BlockNote's onChange doesn't fire
        // for programmatic insertions.
        const nextMd = await serializeCurrentDocument();
        publishMarkdown(nextMd);
        hydratedRef.current = true;
        lastExternalRevisionRef.current = externalRevision;
        return true;
      } catch (e) {
        console.warn('insertAtBlockNoteCaret failed', e);
        return false;
      }
    };

    window.appendToBlockNoteEnd = async (rawMd: string): Promise<string | false> => {
      try {
        if (!rawMd) return false;
        changeSequenceRef.current += 1;
        const currentMd = await serializeCurrentDocument();
        const nextMd = `${currentMd.trimEnd()}\n\n${rawMd.trim()}`.trimStart();
        const prepared = expandDirectMediaLinksToBlocks(prepareAiMarkdownForEditor(rawMd.trim()));
        const blocks = await editor.tryParseMarkdownToBlocks(prepared);
        if (!blocks || blocks.length === 0) return false;
        hydratingRef.current = true;
        const doc = editor.document;
        if (currentMd.trim()) {
          editor.insertBlocks(blocks, doc[doc.length - 1], 'after');
        } else {
          editor.replaceBlocks(doc, blocks);
        }
        window.setTimeout(() => {
          hydratingRef.current = false;
        }, 0);
        publishMarkdown(nextMd);
        hydratedRef.current = true;
        lastExternalRevisionRef.current = externalRevision;
        return nextMd;
      } catch (e) {
        console.warn('appendToBlockNoteEnd failed', e);
        return false;
      }
    };

    return () => {
      delete window.getBlockNoteMarkdown;
      delete window.insertAtBlockNoteCaret;
      delete window.replaceBlockNoteMarkdown;
      delete window.appendToBlockNoteEnd;
    };
  }, [editor, showAIHighlights, serializeCurrentDocument, restoreImageSizes, publishMarkdown, externalRevision]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = content ?? '';
        window.__mindnoteLatestBlockNoteMarkdown = raw;
        const cleaned = expandDirectMediaLinksToBlocks(showAIHighlights
          ? raw
              .replace(/<ai-generated>/g, AI_START_MARKER)
              .replace(/<\/ai-generated>/g, AI_END_MARKER)
          : raw.replace(/<\/?ai-generated>/g, ''));
        // Only skip when the incoming prop is exactly what we last emitted
        // from this editor (i.e. the parent is echoing our own change back).
        // For any other external change (e.g. restoring a previous version
        // from history) we MUST re-hydrate, even if the editor was already
        // hydrated once.
        const aiHighlightToggled = showAIHighlights !== lastShowAIHighlightsRef.current;
        if (!aiHighlightToggled && hydratedRef.current && externalRevision === lastExternalRevisionRef.current && lastEmittedRef.current === raw) return;

        // If the editor is currently focused and the user is typing, skip
        // re-hydration unless an explicit restore bumped externalRevision
        // or the AI-highlight preference just changed (which requires us
        // to rewrite the visible markers).
        const isFocused = containerRef.current?.contains(document.activeElement) ?? false;
        const revisionChanged = externalRevision !== lastExternalRevisionRef.current;
        if (!aiHighlightToggled && hydratedRef.current && isFocused && !revisionChanged) {
          lastEmittedRef.current = raw;
          return;
        }


        const currentMd = hydratedRef.current
          ? await editor.blocksToMarkdownLossy(markTogglesForSerialize(editor.document as any) as any)
          : '';
        if (cancelled) return;
        if (hydratedRef.current && currentMd.trim() === cleaned.trim()) {
          lastEmittedRef.current = raw;
          lastExternalRevisionRef.current = externalRevision;
          return;
        }
        if (cleaned.trim().length > 0) {
          const blocks = await editor.tryParseMarkdownToBlocks(cleaned);
          if (cancelled) return;
          if (blocks.length > 0) {
            restoreImageSizes(blocks);
            unmarkTogglesAfterParse(blocks);
            changeSequenceRef.current += 1;
            hydratingRef.current = true;
            editor.replaceBlocks(editor.document, blocks);
            window.setTimeout(() => {
              hydratingRef.current = false;
            }, 0);
          }
        } else if (hydratedRef.current) {
          // Restored to empty content — clear the editor.
          try {
            changeSequenceRef.current += 1;
            hydratingRef.current = true;
            editor.replaceBlocks(editor.document, [
              { type: 'paragraph' } as any,
            ]);
            window.setTimeout(() => {
              hydratingRef.current = false;
            }, 0);
          } catch (e) {
            hydratingRef.current = false;
            console.warn('BlockNote: failed to clear blocks', e);
          }
        }
        hydratedRef.current = true;
        lastEmittedRef.current = raw;
        lastExternalRevisionRef.current = externalRevision;
        lastShowAIHighlightsRef.current = showAIHighlights;

      } catch (e) {
        console.warn('BlockNote: failed to parse markdown', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [content, externalRevision, editor, showAIHighlights, restoreImageSizes]);

  React.useEffect(() => {
    return () => {
      delete window.__mindnoteLatestBlockNoteMarkdown;
    };
  }, []);

  const handleChange = React.useCallback(async () => {
    if (hydratingRef.current) return;
    try {
      const changeSequence = ++changeSequenceRef.current;
      const md = await serializeCurrentDocument();
      if (changeSequence !== changeSequenceRef.current) return;
      publishMarkdown(md);
    } catch (e) {
      console.warn('BlockNote: failed to serialize markdown', e);
    }
  }, [serializeCurrentDocument, publishMarkdown]);

  React.useEffect(() => {
    if (!editable) return;
    const container = containerRef.current;
    if (!container) return;
    let timeout: number | undefined;
    const syncAfterMediaResize = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Only sync when the interaction is actually on a media resize handle
      // inside this editor. Otherwise unrelated clicks (e.g. on dialog
      // buttons) would trigger handleChange and clobber pending content
      // updates such as restoring a previous version.
      const isResizeHandle = !!target.closest(
        '[class*="resize-handle" i], [class*="resizer" i], [data-resize-handle]'
      );
      if (!isResizeHandle) return;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        void handleChange();
      }, 0);
    };
    container.addEventListener('mouseup', syncAfterMediaResize);
    container.addEventListener('touchend', syncAfterMediaResize);
    return () => {
      window.clearTimeout(timeout);
      container.removeEventListener('mouseup', syncAfterMediaResize);
      container.removeEventListener('touchend', syncAfterMediaResize);
    };
  }, [editable, handleChange]);

  React.useEffect(() => {
    if (!editable || !isIOS) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    const toolbarTriggerSelector = [
      '.bn-mobile-formatting-toolbar [data-test="createLink"]',
      '.bn-formatting-toolbar [data-test="createLink"]',
      '.bn-link-toolbar [data-test="createLink"]',
      '.bn-mobile-formatting-toolbar .bn-select',
      '.bn-formatting-toolbar .bn-select',
      '.bn-mobile-formatting-toolbar [aria-haspopup="listbox"]',
      '.bn-formatting-toolbar [aria-haspopup="listbox"]',
    ].join(', ');

    const menuItemSelector = [
      '.bn-ak-menu-item',
      '.bn-ak-select-item',
      '.bn-suggestion-menu-item',
      '.bn-color-picker-dropdown [role="menuitem"]',
      '.bn-color-picker-dropdown .bn-ak-menu-item',
    ].join(', ');

    const isDisabled = (el: HTMLElement) =>
      el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true';

    const recordTouchStart = (event: TouchEvent) => {
      const t = event.touches[0];
      if (!t) return;
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      touchStartTime = Date.now();
    };

    const restoreSelection = () => {
      const range = lastSelectionRangeRef.current;
      const root = containerRef.current;
      if (!range || !root) return;
      if (!root.contains(range.commonAncestorContainer)) return;

      const selection = window.getSelection();
      if (!selection) return;
      selection.removeAllRanges();
      selection.addRange(range.cloneRange());
    };

    const bridgeTouchTapToClick = (event: TouchEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Buttons in the formatting / link toolbars (Paragraph dropdown
      // trigger, Link icon, etc). We must NOT focus these — focusing the
      // button blurs ProseMirror, the selection is lost, and BlockNote
      // immediately tears down the formatting toolbar before the popover
      // can render.
      const toolbarTrigger = target.closest<HTMLElement>(toolbarTriggerSelector);

      // Items inside an already-opened dropdown / menu. Safe to focus.
      const menuItem = target.closest<HTMLElement>(menuItemSelector);

      const interactive = toolbarTrigger || menuItem;
      if (!interactive) return;
      if (isDisabled(interactive)) return;

      const t = event.changedTouches[0];
      const dx = t ? Math.abs(t.clientX - touchStartX) : 0;
      const dy = t ? Math.abs(t.clientY - touchStartY) : 0;
      const dt = Date.now() - touchStartTime;
      if (dx > 8 || dy > 8 || dt > 500) return;

      event.preventDefault();

      if (toolbarTrigger) {
        restoreSelection();
        // Synchronous click, no focus shift — keeps the editor selection
        // alive so the formatting toolbar (and its popover children) stay
        // mounted long enough for the dropdown to open.
        interactive.click();
      } else {
        window.requestAnimationFrame(() => {
          interactive.focus?.();
          interactive.click();
        });
      }
    };

    document.addEventListener('touchstart', recordTouchStart, { passive: true, capture: true });
    document.addEventListener('touchend', bridgeTouchTapToClick, { passive: false, capture: true });

    const clampColorPickers = () => {
      const selectors = [
        '.bn-ak-popover',
        '.mantine-Menu-dropdown',
        '.mantine-Popover-dropdown',
      ];
      document.querySelectorAll<HTMLElement>(selectors.join(', ')).forEach((el) => {
        // The color picker is CSS-fixed and centered on mobile — never shift it.
        if (el.classList.contains('bn-color-picker-dropdown') || el.closest('.bn-color-picker-dropdown')) {
          el.style.marginLeft = '';
          return;
        }

        // The formatting toolbar wrapper: don't width-clamp it (that hides
        // the left controls), but still shift it horizontally into the
        // viewport just like the "+" / block side menu does naturally.
        const isFormattingToolbarWrapper = !!el.querySelector(
          '.bn-mobile-formatting-toolbar, .bn-formatting-toolbar'
        );
        if (isFormattingToolbarWrapper) {
          const rect = el.getBoundingClientRect();
          if (rect.width === 0 || rect.height === 0) {
            el.style.marginLeft = '';
            return;
          }
          const overflowRight = rect.right - (window.innerWidth - 8);
          const overflowLeft = 8 - rect.left;
          const currentMargin = parseFloat(el.style.marginLeft || '0') || 0;
          let nextMargin = currentMargin;
          if (overflowRight > 0) nextMargin = currentMargin - Math.ceil(overflowRight);
          else if (overflowLeft > 0) nextMargin = currentMargin + Math.ceil(overflowLeft);
          if (nextMargin !== currentMargin) {
            el.style.marginLeft = `${nextMargin}px`;
          }
          return;
        }


        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
          el.style.marginLeft = '';
          return;
        }

        // First, constrain width to viewport so it doesn't extend past the right edge.
        const maxW = Math.min(280, window.innerWidth - 24);
        if (rect.width > maxW) {
          el.style.maxWidth = `${maxW}px`;
          el.style.width = `${maxW}px`;
        }

        // Re-measure after width clamp, then shift horizontally into viewport
        // using marginLeft (floating-ui sets transform, so margin is safe).
        const r2 = el.getBoundingClientRect();
        const overflowRight = r2.right - (window.innerWidth - 8);
        const overflowLeft = 8 - r2.left;
        const currentMargin = parseFloat(el.style.marginLeft || '0') || 0;
        let nextMargin = currentMargin;
        if (overflowRight > 0) nextMargin = currentMargin - Math.ceil(overflowRight);
        else if (overflowLeft > 0) nextMargin = currentMargin + Math.ceil(overflowLeft);
        if (nextMargin !== currentMargin) {
          el.style.marginLeft = `${nextMargin}px`;
        }
      });
    };

    const mutationObserver = new MutationObserver(() => {
      window.requestAnimationFrame(clampColorPickers);
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'hidden', 'data-open'],
    });

    window.addEventListener('resize', clampColorPickers);
    window.addEventListener('scroll', clampColorPickers, true);
    window.requestAnimationFrame(clampColorPickers);

    return () => {
      document.removeEventListener('touchstart', recordTouchStart, true);
      document.removeEventListener('touchend', bridgeTouchTapToClick, true);
      mutationObserver.disconnect();
      window.removeEventListener('resize', clampColorPickers);
      window.removeEventListener('scroll', clampColorPickers, true);
    };
  }, [editable, isIOS]);

  // Expose the note's chosen color globally so the slash-menu popover
  // (rendered in a portal, outside our wrapper) can pick it up.
  React.useEffect(() => {
    document.documentElement.style.setProperty('--note-bg', backgroundColor);
    // Also drive BlockNote's own menu/tooltip background tokens so portaled
    // popovers (block-type dropdown, color picker, etc.) render solid in
    // both light and dark mode instead of inheriting a translucent default.
    document.documentElement.style.setProperty('--bn-colors-menu-background', backgroundColor);
    document.documentElement.style.setProperty('--bn-colors-tooltip-background', backgroundColor);
  }, [backgroundColor]);

  const style = React.useMemo<React.CSSProperties>(
    () => ({
      backgroundColor,
      ['--bn-colors-editor-background' as any]: 'transparent',
      ['--bn-colors-menu-background' as any]: backgroundColor,
      ['--bn-colors-tooltip-background' as any]: backgroundColor,
    }),
    [backgroundColor]
  );

  const applyIOSBlockItem = React.useCallback((itemId: string) => {
    const item = getIOSBlockItems(editor).find((entry) => entry.id === itemId);
    if (!item) return;

    const blockIds = iosBlockMenuState.selectedBlockIds;
    let lastBlockId: string | undefined;

    // Focus the editor immediately before making structural changes. On iOS,
    // ProseMirror needs focus to correctly map an existing text selection
    // when the block type changes (especially to checklists which insert
    // non-editable DOM nodes like the checkbox wrapper).
    editor.focus();

    editor.transact(() => {
      // Root cause: when iOS Safari keeps an inline text-range selection active,
      // converting that block into BlockNote's checklist node can fail because
      // the checklist injects a non-editable checkbox wrapper before the text.
      // Normalizing to a block selection first matches BlockNote's native block
      // transform behavior and avoids the broken selection mapping.
      if (item.type === 'checkListItem' && blockIds.length > 0) {
        try {
          editor.setSelection(blockIds[0], blockIds[blockIds.length - 1]);
        } catch {
          // Fallback to the block updates below if selection normalization fails.
        }
      }

      for (const blockId of blockIds) {
        const update: any = { type: item.type };
        if (item.props) update.props = item.props;
        try {
          editor.updateBlock(blockId, update);
          lastBlockId = blockId;
        } catch (e) {
          console.warn('[iOS block menu] updateBlock failed', e);
        }
      }
    });

    setIosBlockMenuState((current) => ({ ...current, open: false, activeItemId: itemId }));

    // After the dialog closes and focus returns, place the caret in the
    // converted block so the user can keep typing.
    if (lastBlockId) {
      const targetId = lastBlockId;
      setTimeout(() => {
        try {
          editor.focus();
          editor.setTextCursorPosition(targetId, 'end');
        } catch { /* noop */ }
      }, 250);
    }
  }, [editor, iosBlockMenuState.selectedBlockIds]);
  const submitIOSLinkDialog = React.useCallback(() => {
    const normalizedUrl = normalizeLinkUrl(iosLinkDialogState.url);
    if (!normalizedUrl) return;

    editor.focus();
    editor.editLink(normalizedUrl, iosLinkDialogState.text, iosLinkDialogState.from);
    setIosLinkDialogState((current) => ({ ...current, open: false }));
  }, [editor, iosLinkDialogState]);

  const IOSFormattingToolbarComponent = React.useCallback(() => (
    <IOSFormattingToolbar
      onOpenBlockMenu={({ activeItemId, selectedBlockIds }) => {
        setIosBlockMenuState({ open: true, activeItemId, selectedBlockIds });
      }}
      onOpenLinkDialog={({ from, text, url }) => {
        setIosLinkDialogState({ open: true, from, text, url });
      }}
    />
  ), []);

  return (
    <div
      ref={containerRef}
      className="rounded-md border border-border bn-note-surface"
      style={style}
    >
      <BlockNoteView
        editor={editor}
        editable={editable}
        onChange={handleChange}
        theme={theme === 'dark' ? 'dark' : 'light'}
        formattingToolbar={!isIOS}
        filePanel={false}
        slashMenu={false}
      >
        <FilePanelController filePanel={SingleEmbedFilePanel} />
        <SuggestionMenuController
          triggerCharacter={'/'}
          getItems={async (query) =>
            filterSuggestionItems(
              getDefaultReactSlashMenuItems(editor).map((item) => ({
                ...item,
                badge: undefined,
                subtext: undefined,
              })),
              query,
            )
          }
        />
        {isIOS && editable && (
          <FormattingToolbarController formattingToolbar={IOSFormattingToolbarComponent} />
        )}
      </BlockNoteView>

      <Dialog
        modal={false}
        open={iosBlockMenuState.open}
        onOpenChange={(open) => setIosBlockMenuState((current) => ({ ...current, open }))}
      >
        <DialogContent
          className="max-w-[min(92vw,22rem)]"
          style={{ backgroundColor }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Block type</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            {getIOSBlockItems(editor).map((item) => {
              const Icon = item.icon;
              const selected = iosBlockMenuState.activeItemId === item.id;
              return (
                <Button
                  key={item.id}
                  type="button"
                  variant={selected ? 'secondary' : 'outline'}
                  className="justify-start"
                  style={{ backgroundColor }}
                  onClick={() => applyIOSBlockItem(item.id)}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={iosLinkDialogState.open}
        onOpenChange={(open) => setIosLinkDialogState((current) => ({ ...current, open }))}
      >
        <DialogContent className="max-w-[min(92vw,24rem)]">
          <DialogHeader>
            <DialogTitle>Insert link</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Input
              value={iosLinkDialogState.url}
              placeholder="https://example.com"
              onChange={(event) => setIosLinkDialogState((current) => ({ ...current, url: event.target.value }))}
            />
            <Input
              value={iosLinkDialogState.text}
              placeholder="Link text"
              onChange={(event) => setIosLinkDialogState((current) => ({ ...current, text: event.target.value }))}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitIOSLinkDialog();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIosLinkDialogState((current) => ({ ...current, open: false }))}>
              Cancel
            </Button>
            <Button type="button" onClick={submitIOSLinkDialog}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlockNoteEditorWrapper;
